import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * WP-04: VNSymbolSearch — moved from src/demo/components/
 * Search box with debounce and keyboard navigation.
 * Widget-scoped: uses useWidgetI18n(), .vnsc- prefix.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useWidgetI18n } from "../../context/WidgetI18nContext";
import { useShadowRoot } from "../../context/VNInvestChartsContext";
import { COMPARISON_COLORS } from "../../constants/comparison";
import { CRYPTO_SYMBOLS, EXCHANGE_OPTIONS, FUTURES_SYMBOLS, TIMEFRAME_ALIASES, VN30_SYMBOLS } from "../../constants/symbolSearch";
import { useRecentSymbols } from "../../hooks/useRecentSymbols";
import { useSymbolSearch } from "../../hooks/useSymbolSearch";
import { isClickOutside } from "../../utils/clickOutside";
import styles from "../../styles/symbol-search.module.css";
// UX-02: WAI-ARIA combobox contract — the input owns the listbox via
// `aria-controls` and points at the highlighted option with
// `aria-activedescendant` (focus stays in the input while arrowing).
const LISTBOX_ID = "vnsc-symbol-search-listbox";
export function VNSymbolSearch({ adapter, client, value, onChange, onItemSelect, onFocusGuard, quickEntryPrefix, onTimeframeChange, mode = "symbol", compareOpen, onCompareClose, onAddCompareSymbol, onRemoveCompareSymbol, compareExistingOverlays = [], compareOverlayEntries, onUpdateCompareOverlay, compare: compareProp, defaultAction, allowSwitch, mainSymbol, open: openProp, onOpenChange, flat = false, }) {
    const isCompare = mode === "compare";
    const isUnified = mode === "unified";
    // P2-02 compat shim: loose compare* props map onto the unified object (warn once in dev).
    const hasLegacyCompare = compareOpen !== undefined || onCompareClose !== undefined
        || onAddCompareSymbol !== undefined || onRemoveCompareSymbol !== undefined
        || compareExistingOverlays.length > 0 || compareOverlayEntries !== undefined
        || onUpdateCompareOverlay !== undefined;
    const warnedRef = useRef(false);
    if (hasLegacyCompare && !compareProp && !warnedRef.current) {
        warnedRef.current = true;
        if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
            console.warn("[VNSymbolSearch] compare* props are deprecated — pass compare={{...}} instead.");
        }
    }
    const compare = compareProp ?? (hasLegacyCompare ? {
        existing: compareExistingOverlays,
        entries: compareOverlayEntries,
        onAdd: onAddCompareSymbol ? (id) => onAddCompareSymbol(id) : () => { },
        onRemove: onRemoveCompareSymbol ?? (() => { }),
        onUpdate: onUpdateCompareOverlay,
        open: compareOpen,
        onClose: onCompareClose,
    } : undefined);
    const cmpExisting = compare?.existing ?? compareExistingOverlays;
    const cmpEntries = compare?.entries ?? compareOverlayEntries;
    const cmpOnRemove = compare?.onRemove ?? onRemoveCompareSymbol;
    const cmpOnUpdate = compare?.onUpdate ?? onUpdateCompareOverlay;
    /** Effective default pick action: compare dialog can never switch the main symbol. */
    const effectiveDefaultAction = defaultAction ?? (isCompare ? "compare" : "switch");
    const canSwitch = allowSwitch ?? effectiveDefaultAction === "switch";
    const isMainSymbol = useCallback((sym) => !!mainSymbol && sym.toUpperCase() === mainSymbol.toUpperCase(), [mainSymbol]);
    const isControlled = openProp !== undefined;
    const { t } = useWidgetI18n();
    const shadowRoot = useShadowRoot();
    const [internalOpen, setInternalOpen] = useState(false);
    const open = isControlled ? openProp : internalOpen;
    /** Unified open-state setter: updates local state + fires onOpenChange. */
    const setOpen = useCallback((next) => {
        if (isControlled) {
            onOpenChange?.(next);
        }
        else {
            setInternalOpen(next);
        }
    }, [isControlled, onOpenChange]);
    const [exchangeFilter, setExchangeFilter] = useState("ALL");
    const [recentItems, saveRecentItem] = useRecentSymbols();
    const { query, setQuery, results, loading, highlightIndex, setHighlightIndex, search } = useSymbolSearch({
        adapter, client, exchangeFilter, compareExisting: (isCompare || (isUnified && compare)) ? cmpExisting : undefined,
    });
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);
    useEffect(() => {
        function handleClick(e) {
            const me = e;
            if (isClickOutside(wrapperRef.current, me)) {
                if (isCompare) {
                    onCompareClose?.();
                }
                else {
                    setOpen(false);
                }
            }
        }
        // Use shadowRoot for capture-phase listeners in Shadow DOM mode
        const target = shadowRoot ?? document;
        target.addEventListener("mousedown", handleClick, { capture: true });
        return () => {
            target.removeEventListener("mousedown", handleClick, { capture: true });
            if (debounceRef.current)
                clearTimeout(debounceRef.current);
        };
    }, [shadowRoot, isCompare, onCompareClose]);
    // Parse trailing timeframe suffix from query (e.g. "BTCUSDT 1D" -> { symbol: "BTCUSDT", timeframe: "1D" })
    function parseTimeframeSuffix(raw) {
        const trimmed = raw.trim();
        const parts = trimmed.split(/\s+/);
        if (parts.length < 2)
            return { symbol: trimmed };
        const last = parts[parts.length - 1];
        // Try exact match first (preserves case for 1M vs 1m), then lowercase fallback
        const matched = TIMEFRAME_ALIASES[last] ?? TIMEFRAME_ALIASES[last.toLowerCase()];
        if (matched) {
            return { symbol: parts.slice(0, -1).join(" "), timeframe: matched };
        }
        return { symbol: trimmed };
    }
    /** Search pass + dropdown open-state (symbol mode only).
     * A background re-search (typing / exchange filter) may OPEN the dropdown
     * but must NEVER auto-close it — closing is reserved for explicit actions
     * (select, Escape, outside click). Otherwise clicking HOSE/HNX/UPCOM with
     * 0 filtered results hides the panel the user is interacting with. */
    const searchWithOpen = useCallback(async (q, exFilter, keepOpen = false) => {
        const count = await search(q, exFilter);
        if (count !== null && !isCompare && (count > 0 || keepOpen))
            setOpen(true);
    }, [search, isCompare, setOpen]);
    useEffect(() => {
        if (!query)
            return;
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => void searchWithOpen(query, exchangeFilter, true), 150);
    }, [exchangeFilter, query, searchWithOpen]);
    useEffect(() => {
        if (quickEntryPrefix) {
            setQuery(quickEntryPrefix);
            setOpen(true);
            searchWithOpen(quickEntryPrefix, exchangeFilter);
        }
    }, [quickEntryPrefix, exchangeFilter, searchWithOpen]);
    // Move cursor to end when quick entry sets query from outside
    useEffect(() => {
        if (query && wrapperRef.current) {
            const input = wrapperRef.current.querySelector("input");
            if (input && document.activeElement === input) {
                input.setSelectionRange(query.length, query.length);
            }
        }
    }, [query]);
    function handleInputChange(e) {
        const q = e.target.value;
        setQuery(q);
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        if (!q) {
            void search("", exchangeFilter);
            if (!isCompare)
                setOpen(true);
            return;
        }
        debounceRef.current = setTimeout(() => void searchWithOpen(q, exchangeFilter), 300);
    }
    function handleSelect(item) {
        setQuery(item.symbol);
        setOpen(false);
        // Check if the user typed a timeframe suffix (e.g. selected "VIC" from query "VIC 1D")
        const { timeframe } = parseTimeframeSuffix(query);
        saveRecentItem(item);
        onChange?.(item.symbol);
        if (timeframe && onTimeframeChange)
            onTimeframeChange(timeframe);
        onItemSelect?.(item);
    }
    function commitRawQuery() {
        const raw = query.trim().toUpperCase();
        if (!raw)
            return;
        const { symbol, timeframe } = parseTimeframeSuffix(raw);
        setQuery(symbol);
        setOpen(false);
        onChange?.(symbol);
        if (timeframe && onTimeframeChange)
            onTimeframeChange(timeframe);
    }
    function handleCompareAddSymbol(item) {
        onAddCompareSymbol?.(item.symbol);
        onCompareClose?.();
        setQuery("");
        void search("", exchangeFilter);
    }
    /** Unified Switch (P2): main-symbol change — caller closes the modal via onChange. */
    function handleUnifiedSwitch(item) {
        const { timeframe } = parseTimeframeSuffix(query);
        saveRecentItem(item);
        setQuery("");
        setOpen(false);
        onChange?.(item.symbol);
        if (timeframe && onTimeframeChange)
            onTimeframeChange(timeframe);
        onItemSelect?.(item);
    }
    /** Unified +Compare (P2): add overlay, KEEP the modal open for the next pick. */
    function handleUnifiedCompare(item) {
        if (!compare || isMainSymbol(item.symbol))
            return;
        saveRecentItem(item);
        compare.onAdd(item.symbol);
        setQuery("");
        void search("", exchangeFilter);
    }
    function handleKeyDown(e) {
        if (e.key === "ArrowDown") {
            if (results.length === 0)
                return;
            e.preventDefault();
            setHighlightIndex(Math.min(highlightIndex + 1, results.length - 1));
        }
        else if (e.key === "ArrowUp") {
            if (results.length === 0)
                return;
            e.preventDefault();
            setHighlightIndex(Math.max(highlightIndex - 1, 0));
        }
        else if (e.key === "Enter") {
            e.preventDefault();
            const selected = results.length > 0 ? results[highlightIndex] : null;
            if (selected) {
                if (isUnified) {
                    if (isMainSymbol(selected.symbol))
                        return;
                    if (e.shiftKey || effectiveDefaultAction === "compare") {
                        handleUnifiedCompare(selected);
                    }
                    else {
                        handleUnifiedSwitch(selected);
                    }
                }
                else if (isCompare) {
                    handleCompareAddSymbol(selected);
                }
                else {
                    handleSelect(selected);
                }
                return;
            }
            if (!isCompare)
                commitRawQuery();
        }
        else if (e.key === "Escape") {
            if (isCompare) {
                onCompareClose?.();
            }
            else {
                setOpen(false);
            }
        }
    }
    function handleFocus() {
        if (onFocusGuard) {
            onFocusGuard();
            return;
        }
        setOpen(true);
    }
    const dropdownOpen = isCompare ? compareOpen : open;
    function handleResultSelect(item) {
        if (isMainSymbol(item.symbol))
            return;
        if (isUnified) {
            if (effectiveDefaultAction === "compare") {
                handleUnifiedCompare(item);
            }
            else {
                handleUnifiedSwitch(item);
            }
        }
        else if (isCompare) {
            handleCompareAddSymbol(item);
        }
        else {
            handleSelect(item);
        }
    }
    return (_jsxs("div", { ref: wrapperRef, className: styles.wrapper, children: [_jsx("input", { className: styles.input, type: "text", value: query, placeholder: t("symbolSearch.placeholder", { defaultValue: "Search symbol..." }), onChange: handleInputChange, onKeyDown: handleKeyDown, onFocus: handleFocus, autoFocus: true, "aria-autocomplete": "list", "aria-haspopup": "listbox", role: "combobox", "aria-expanded": !!dropdownOpen, "aria-controls": LISTBOX_ID, "aria-activedescendant": dropdownOpen && results.length > 0 && highlightIndex >= 0 ? `${LISTBOX_ID}-opt-${highlightIndex}` : undefined }), dropdownOpen && (_jsxs("div", { className: flat ? `${styles.dropdown} ${styles.dropdownFlat}` : styles.dropdown, children: [(isCompare || isUnified) && cmpExisting.length > 0 && (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.section, children: [_jsx("div", { className: styles.sectionLabel, children: t("compare.existing", { defaultValue: "Compared symbols" }) }), cmpExisting.map((sym, idx) => {
                                        const entry = cmpEntries?.find((e) => e.symbolId === sym);
                                        const color = entry?.color ?? COMPARISON_COLORS[idx % COMPARISON_COLORS.length];
                                        const visible = entry?.visible ?? true;
                                        const normalize = entry?.normalizeToBaseline ?? false;
                                        return (_jsxs("div", { className: styles.overlayRow, children: [_jsxs("div", { className: styles.overlayRowLeft, children: [_jsx("span", { className: styles.colorDot, style: { background: color } }), _jsx("span", { className: styles.symbol, children: sym })] }), _jsxs("div", { style: { display: "flex", gap: 4, alignItems: "center" }, children: [_jsx("button", { type: "button", className: styles.addBtn, style: { color: "inherit", borderColor: "#555" }, onClick: () => cmpOnUpdate?.(sym, { visible: !visible }), title: visible ? t("compare.hide", { defaultValue: "Hide" }) : t("compare.show", { defaultValue: "Show" }), children: visible ? "👁" : "◌" }), _jsx("button", { type: "button", className: styles.addBtn, style: { color: normalize ? "#2196F3" : "#aaa", borderColor: "#555", minWidth: 44 }, onClick: () => cmpOnUpdate?.(sym, { normalizeToBaseline: !normalize }), title: normalize ? t("compare.absolute", { defaultValue: "Price" }) : t("compare.normalize", { defaultValue: "% Change" }), children: normalize ? "%" : "₫" }), _jsx("button", { type: "button", className: styles.removeBtn, onClick: () => cmpOnRemove?.(sym), children: "\u2715" })] })] }, sym));
                                    })] }), _jsx("hr", { className: styles.divider })] })), _jsx("div", { className: styles.exchangeBar, children: EXCHANGE_OPTIONS.map((ex) => (_jsx("button", { type: "button", className: `${styles.exBtn} ${exchangeFilter === ex ? styles.exBtnActive : ""}`, onMouseDown: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setExchangeFilter(ex);
                                if (!isCompare)
                                    setOpen(true);
                            }, children: ex === "ALL" ? "All" : ex }, ex))) }), query && results.length > 0 && (_jsx("ul", { role: "listbox", id: LISTBOX_ID, "aria-label": t("symbolSearch.placeholder", { defaultValue: "Search symbol..." }), children: results.map((item, idx) => {
                            const isCurrentMain = isMainSymbol(item.symbol);
                            return (_jsxs("li", { role: "option", id: `${LISTBOX_ID}-opt-${idx}`, "aria-selected": idx === highlightIndex, "aria-disabled": isCurrentMain || undefined, className: `${styles.item} ${idx === highlightIndex ? styles.itemHighlighted : ""} ${isCurrentMain ? styles.itemDisabled : ""}`, onMouseDown: () => handleResultSelect(item), children: [(isCompare || (isUnified && compare)) && (_jsx("span", { className: styles.colorDot, style: { background: COMPARISON_COLORS[(cmpExisting.length + idx) % COMPARISON_COLORS.length] } })), _jsx("span", { className: styles.symbol, children: item.symbol }), item.exchange && (_jsx("span", { className: `${styles.exBadge} ${styles[`exBadge${item.exchange.charAt(0).toUpperCase() + item.exchange.slice(1).toLowerCase()}`]}`, children: item.exchange })), item.company_name && (_jsx("span", { className: styles.name, children: item.company_name })), isCurrentMain ? (_jsx("span", { className: styles.currentBadge, children: t("compare.currentSymbol", { defaultValue: "Current" }) })) : isUnified && compare ? (_jsxs("span", { className: styles.unifiedRowActions, children: [canSwitch && (_jsx("button", { type: "button", className: `${styles.addBtn} ${styles.switchBtn}`, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => { e.stopPropagation(); handleUnifiedSwitch(item); }, children: t("symbolSearch.switch", { defaultValue: "Switch" }) })), _jsxs("button", { type: "button", className: styles.addBtn, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => { e.stopPropagation(); handleUnifiedCompare(item); }, children: ["+ ", t("common.compare", { defaultValue: "Compare" })] })] })) : isCompare && (_jsx("button", { type: "button", className: styles.addBtn, onClick: (e) => { e.stopPropagation(); handleCompareAddSymbol(item); }, children: t("compare.add", { defaultValue: "Add" }) }))] }, item.symbol));
                        }) })), query && loading && results.length === 0 && (_jsx("div", { className: styles.emptyState, children: _jsx("span", { className: styles.loadingText, children: t("common.loading", { defaultValue: "Searching…" }) }) })), query && !loading && results.length === 0 && (_jsx("div", { className: styles.emptyState, children: _jsx("span", { className: styles.emptyText, children: t("symbolSearch.noResults", { defaultValue: "No results for \"{query}\"" }).replace("{query}", query) }) })), !query && results.length === 0 && !loading && (_jsxs(_Fragment, { children: [recentItems.length > 0 && (_jsxs("div", { className: styles.section, children: [_jsx("div", { className: styles.sectionLabel, children: t("symbolSearch.recent", { defaultValue: "Recent" }) }), _jsx("div", { className: styles.chipRow, children: recentItems.map((item) => (_jsx("button", { type: "button", className: styles.chip, onMouseDown: () => handleResultSelect(item), children: item.symbol }, item.symbol))) })] })), _jsxs("div", { className: styles.section, children: [_jsx("div", { className: styles.sectionLabel, children: t("symbolSearch.futures", { defaultValue: "Futures" }) }), _jsx("div", { className: styles.chipRow, children: FUTURES_SYMBOLS.map((item) => (_jsx("button", { type: "button", className: `${styles.chip} ${styles.chipFutures}`, onMouseDown: () => handleResultSelect(item), children: item.symbol }, item.symbol))) })] }), _jsxs("div", { className: styles.section, children: [_jsx("div", { className: styles.sectionLabel, children: t("symbolSearch.vn30", { defaultValue: "VN30" }) }), _jsx("div", { className: styles.vn30Grid, children: VN30_SYMBOLS.map((sym) => (_jsx("button", { type: "button", className: `${styles.chip} ${styles.chipVn30}`, onMouseDown: () => handleResultSelect({ symbol: sym, company_name: "", exchange: "HOSE" }), children: sym }, sym))) })] }), _jsxs("div", { className: styles.section, children: [_jsx("div", { className: styles.sectionLabel, children: t("symbolSearch.crypto", { defaultValue: "Crypto" }) }), _jsx("div", { className: styles.chipRow, children: CRYPTO_SYMBOLS.map((item) => (_jsx("button", { type: "button", className: `${styles.chip} ${styles.chipCrypto}`, onMouseDown: () => handleResultSelect(item), children: item.symbol }, item.symbol))) })] })] }))] }))] }));
}
/** SSOT alias (SYMBOL_SEARCH P1): single search component for symbol + compare. */
export { VNSymbolSearch as SymbolSearch };
